import { TextField } from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import locale from "date-fns/locale/pt-BR";
import { useEffect, useRef, useState } from "react";
import ClickAwayListener from "../click-away-listener/click-away-listener.component";
import IconButtonCustom from "../iconButton/IconButtonCustom.component";
import InputAdornment from "../inputAdornment/inputAdornment.component";
import Paper from "../paper/paper.component";
import Popper from "../popper/popper.component";

function formatDateInput(value) {
  const digits = value.replace(/\D/g, "");
  let result = "";

  if (digits.length > 0) result = digits.substring(0, 2);
  if (digits.length >= 3) result += "/" + digits.substring(2, 4);
  if (digits.length >= 5) result += "/" + digits.substring(4, 8);

  return result;
}

function parseStringToDate(value) {
  let date = value;
  if (typeof value === "string") {
    date = new Date(value);
  }
  return date;
}

function formatDateString(value) {
  if (!value) return "";
  const date = parseStringToDate(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

function parseDate(day, month, year) {
  if (!day || !month || !year) return null;

  return new Date(year, month - 1, day);
}

function handleError(setError, label) {
  if (setError) {
    setError((prevState) =>
      prevState.includes(label) ? prevState : [...prevState, label],
    );
  }
}

function removeError(setError, label) {
  if (setError) {
    setError((prevState) => prevState.filter((item) => item !== label));
  }
}

const DatePicker = ({
  id,
  className,
  label,
  name,
  value,
  onChange,
  onClick,
  minDate,
  maxDate,
  disableTextfield,
  disabled,
  error,
  required,
  variant,
  shrink,
  margin,
  sx,
  setError,
}) => {
  const defaultSx = {
    margin: margin ? "1rem 1rem 0.5rem auto" : "auto",
    width: "100%",
  };

  const [inputValue, setInputValue] = useState(formatDateString(value));
  const [dateValue, setDateValue] = useState(parseStringToDate(value));
  const [timeDate, setTimeDate] = useState(value && new Date(value).getTime());
  const [open, setOpen] = useState(false);
  const [internalError, setInternalError] = useState(false);

  const anchorRef = useRef(null);
  const popperContentRef = useRef(null);

  useEffect(() => {
    const time = value && new Date(value).getTime();
    if (timeDate !== time) {
      const date = parseStringToDate(value);
      setInputValue(formatDateString(date));
      setDateValue(date);
      setTimeDate(time);
    }
  }, [value]);

  const handleClickAway = (event) => {
    const anchorEl = anchorRef.current;
    const popperEl = popperContentRef.current;
    if (
      (anchorEl && anchorEl.contains(event.target)) ||
      (popperEl && popperEl.contains(event.target))
    ) {
      return;
    }

    setOpen(false);
  };

  const handleChange = (event) => {
    const data = formatDateInput(event.target.value);
    setInputValue(data);

    const [day, month, year] = data.split("/");
    const parsed = parseDate(day, month, year);

    if (parsed) {
      const isValid =
        parsed.getDate() === parseInt(day, 10) &&
        parsed.getMonth() + 1 === parseInt(month, 10) &&
        year.length === 4 &&
        parsed.getFullYear() === parseInt(year, 10);
      setInternalError(!isValid);

      if (isValid) {
        if ((minDate && parsed < minDate) || (maxDate && parsed > maxDate)) {
          setInternalError(true);
          handleError(setError, label);
        } else {
          setInternalError(false);
          removeError(setError, label);
          setDateValue(parsed);
          onChange(parsed);
        }
      } else {
        handleError(setError, label);
      }
    } else {
      setInternalError(false);
      if (onChange && !data) onChange(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <TextField
        id={id}
        className={className}
        label={label}
        name={name}
        ref={anchorRef}
        value={inputValue}
        onChange={handleChange}
        onClick={onClick}
        placeholder="DD/MM/AAAA"
        disabled={disableTextfield || disabled}
        error={internalError || error}
        helperText={internalError || error ? "Data inválida" : ""}
        required={required}
        variant={variant}
        sx={{
          ...defaultSx,
          ...sx,
        }}
        InputLabelProps={{ shrink }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButtonCustom
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                  p: 0,
                  zIndex: 1000,
                  backgroundColor: "white",
                  marginRight: -0.5,
                }}
                tabIndex={-1}
                disabled={disabled}
              >
                <i
                  className={`${
                    disabled ? "ph" : "ph-duotone"
                  } ph-calendar-dots`}
                  style={{
                    fontSize: 26,
                    color: disabled ? "darkgray" : "inherit",
                  }}
                ></i>
              </IconButtonCustom>
            </InputAdornment>
          ),
        }}
      />

      <ClickAwayListener
        onClickAway={handleClickAway}
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
      >
        <div ref={popperContentRef}>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            disablePortal={false}
            style={{ zIndex: 2000 }}
          >
            <Paper elevation={8}>
              <DateCalendar
                value={dateValue}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(newValue) => {
                  setDateValue(newValue);
                  if (newValue) {
                    const dateString = formatDateString(newValue);
                    setInputValue(dateString);
                    setOpen(false);
                    removeError(setError, label);
                    setInternalError(false);
                  }
                  onChange(newValue);
                }}
              />
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>
    </LocalizationProvider>
  );
};

export default DatePicker;
